/**
 * Framework-agnostic theme bootstrap shared by every viewer.
 *
 * Two CSS contracts this module has to satisfy together:
 *  - `data-bs-theme` on <html> drives Bootstrap's own light/dark tokens and
 *    the dark `--vscode-*` overrides in vscode.css (light defaults are
 *    ungated `:root`).
 *  - `body.vscode-dark` activates the `--vscode-* → --bs-*` bridge in
 *    base.css (`body[class^="vscode-"]`). We deliberately set only
 *    `vscode-dark` (never `vscode-light`) so light standalone stays on
 *    pure Bootstrap — see the comment at the toggle call below.
 *
 * Nothing here self-executes; callers must invoke the returned function.
 * That keeps the module import-safe for tests/SSR and lets the caller
 * control timing (the inline <script> calls it before first paint).
 */

export type ThemePreference =
  | "system"
  | "light"
  | "dark"
  | "readable-system"
  | "readable-light"
  | "readable-dark";

/**
 * Orthogonal-in-spirit "variant" axis kept separate from light/dark so a
 * readable theme is one small token-override block per base mode rather
 * than a duplicated stylesheet. (The user-facing preference is a flat list
 * — `readable-*` — but it still resolves down to base + variant here.)
 */
export type ThemeVariant = "default" | "readable";

const THEME_PREFERENCES: readonly ThemePreference[] = [
  "system",
  "light",
  "dark",
  "readable-system",
  "readable-light",
  "readable-dark",
];

export const isThemePreference = (value: unknown): value is ThemePreference =>
  THEME_PREFERENCES.some((preference) => preference === value);

/**
 * Read a persisted preference out of the zustand-persist-compatible settings
 * blob both app-local stores write (`{ state: { themePreference } }`).
 * Dependency-free so the inline bootstrap `<script>` can use it; apps pass
 * their own `storageKey` (storage is an app concern — see `createApplyTheme`'s
 * `readPreference` option) and wire it via
 * `readPreference: () => readThemePreference(localStorage, KEY)`.
 */
export const readThemePreference = (
  storage: Pick<Storage, "getItem">,
  storageKey: string
): ThemePreference => {
  // Default (no/invalid stored value) is the readable "Event Colors" variant
  // per product decision; keep in sync with the app userSettings defaults.
  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return "readable-system";
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "state" in parsed) {
      const state = parsed.state;
      if (state && typeof state === "object" && "themePreference" in state) {
        const value = state.themePreference;
        if (isThemePreference(value)) return value;
      }
    }
    return "readable-system";
  } catch {
    return "readable-system";
  }
};

/**
 * Resolved dark/light for a preference — drives the toggle's icon and
 * flip direction. For `system` it reads the OS color scheme. (Host
 * query-param overrides are intentionally ignored: the in-app picker is
 * only surfaced standalone.)
 */
export const resolveIsDark = (value: ThemePreference): boolean => {
  if (value === "dark" || value === "readable-dark") return true;
  if (value === "light" || value === "readable-light") return false;
  // "system" and "readable-system" both follow the host/OS theme below.
  // VS Code webview: trust the host's body class over `matchMedia`, which
  // only reports the OS color scheme and so can disagree with the IDE theme.
  const hostIsDark = hostIsDarkFromBody();
  if (hostIsDark !== null) return hostIsDark;
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
};

/**
 * Decompose any theme name (a preference, a host query param, or a
 * computed system value) into its dark/light + variant axes. Tolerant of the
 * VS Code-style `vscode-dark`/`vscode-light` strings hosts may pass.
 */
const parseThemeName = (
  name: string
): { isDark: boolean; variant: ThemeVariant } => {
  const variant: ThemeVariant = name.startsWith("readable-")
    ? "readable"
    : "default";
  const isDark = name === "dark" || name.endsWith("-dark");
  return { isDark, variant };
};

export type ResolveInput = {
  preference: ThemePreference;
  explicitParam: string | null;
  isVscodeWebview: boolean;
  prefersDark: boolean;
};

export type ResolveOutput =
  | {
      kind: "apply-variant-only";
      variant: ThemeVariant;
      /**
       * The host's resolved dark/light, so the variant token block keyed on
       * `:root[data-bs-theme="dark"]` activates when the host is dark.
       * `null` if we can't tell (no vscode-* body class yet) — caller then
       * leaves `data-bs-theme` untouched.
       */
      hostIsDark: boolean | null;
    }
  | {
      kind: "apply";
      isDark: boolean;
      variant: ThemeVariant;
      toggleBodyClass: boolean;
    };

export const resolveTheme = (input: ResolveInput): ResolveOutput => {
  // Inside VS Code: the in-app preference wins when explicitly set; only on
  // `system` do we defer to VS Code's own theme machinery (or to a host
  // query param, which embedders use to force a specific theme).
  if (input.isVscodeWebview) {
    // In a webview the host injects `--vscode-*` tokens we cannot reliably
    // re-skin, so trying to override light/dark from inside always leaves
    // hybrid messes (host-tinted code blocks on user-picked backgrounds,
    // etc.). The host owns base mode. Event Colors (the readable variant)
    // is safe to override because it only flips `data-theme-variant` and
    // doesn't touch the bridge — apply it without changing the base theme.
    if (
      input.preference === "readable-light" ||
      input.preference === "readable-dark" ||
      input.preference === "readable-system" ||
      !input.explicitParam
    ) {
      // Mirror the host's body class onto `data-bs-theme` so the dark readable
      // token block follows Cursor/VS Code theme changes. Plain webview
      // preferences use the same path to clear a previously selected variant.
      const hostIsDark = hostIsDarkFromBody();
      return {
        kind: "apply-variant-only",
        variant: input.preference.startsWith("readable-")
          ? "readable"
          : "default",
        hostIsDark,
      };
    }
    const parsed = parseThemeName(input.explicitParam);
    return {
      kind: "apply",
      isDark: parsed.isDark,
      variant: parsed.variant,
      toggleBodyClass: false,
    };
  }

  let name: string;
  // `readable-system` keeps the readable variant but follows the OS scheme
  // for base mode — so toggling OS dark/light still flips light/dark while
  // Event Colors stays on.
  if (input.preference !== "system" && input.preference !== "readable-system") {
    name = input.preference;
  } else if (input.explicitParam) {
    name = input.explicitParam;
  } else {
    name = input.prefersDark ? "dark" : "light";
  }
  if (input.preference === "readable-system" && !name.startsWith("readable-")) {
    name = `readable-${name}`;
  }

  const { isDark, variant } = parseThemeName(name);
  return {
    kind: "apply",
    isDark,
    variant,
    toggleBodyClass: true,
  };
};

export type ApplyThemeOptions = {
  /**
   * URL query parameter a host iframe uses to force a theme. inspect uses
   * `inspectLogviewThemeCategory`; scout/hawk use `inspectViewThemeCategory`.
   */
  queryParamName: string;
  /**
   * Read the app's persisted preference. Storage shape/key is an app concern,
   * so each app supplies its own (dependency-free) reader; omit when the app
   * has no preference store and the theme should follow the host/OS only.
   */
  readPreference?: () => ThemePreference;
};

declare global {
  interface Window {
    // One hook per window, intentional: the theme is document-level (one
    // <html data-bs-theme>), so co-resident viewers share it. Don't namespace
    // per app — hosts like hawk call this exact name.
    __APPLY_BROWSER_THEME__?: () => void;
  }
}

// A single stable matchMedia listener delegates to the most recently built
// applyTheme, so repeated createApplyTheme calls (HMR, tests, an app that
// re-inits) never accumulate listeners or leak.
let currentApplyTheme: (() => void) | null = null;
let mediaListenerInstalled = false;
let bodyClassObserverInstalled = false;

const hostIsDarkFromBody = (): boolean | null => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (typeof document === "undefined" || !document.body) return null;
  const cls = document.body.classList;
  // VS Code high contrast: `vscode-high-contrast` is the HC-dark theme,
  // `vscode-high-contrast-light` the HC-light. Both classes are independent
  // tokens (classList is set-based), so order doesn't matter for correctness.
  if (cls.contains("vscode-high-contrast-light")) return false;
  if (cls.contains("vscode-high-contrast")) return true;
  if (cls.contains("vscode-dark")) return true;
  if (cls.contains("vscode-light")) return false;
  return null;
};

// `in` rather than a global declaration: @tsmono/util declares this same
// property with its own VSCodeApi type, and this module stays dependency-free
// for the inline bootstrap <script>, so a second declaration would collide.
const isVscodeWebview = (): boolean =>
  "acquireVsCodeApi" in window && typeof window.acquireVsCodeApi === "function";

const installBodyClassObserver = (): void => {
  if (
    bodyClassObserverInstalled ||
    typeof MutationObserver === "undefined" ||
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    !document.body
  ) {
    return;
  }

  bodyClassObserverInstalled = true;
  let lastClass = document.body.className;
  new MutationObserver(() => {
    if (document.body.className === lastClass) return;
    lastClass = document.body.className;
    // Re-apply so `data-bs-theme`/variant follow the host theme swap; the React
    // icon updates by observing the resulting `data-bs-theme` change.
    currentApplyTheme?.();
  }).observe(document.body, {
    attributes: true,
    attributeFilter: ["class"],
  });
};

const setDocumentBaseScheme = (isDark: boolean): void => {
  const scheme = isDark ? "dark" : "light";
  document.documentElement.setAttribute("data-bs-theme", scheme);
  // Set before the stylesheet loads so the UA canvas, scrollbars, and form
  // controls do not flash white while the dark app background is still loading.
  document.documentElement.style.colorScheme = scheme;
};

/**
 * Build (do not run) the apply-theme function. The returned function is
 * idempotent and safe to call repeatedly (e.g. on a matchMedia change or a
 * preference change). It also registers itself as
 * `window.__APPLY_BROWSER_THEME__` and starts a `prefers-color-scheme`
 * listener on first call.
 */
export const createApplyTheme = (options: ApplyThemeOptions): (() => void) => {
  const applyTheme = (): void => {
    installBodyClassObserver();

    const params = new URLSearchParams(window.location.search);
    const result = resolveTheme({
      preference: options.readPreference ? options.readPreference() : "system",
      explicitParam: params.get(options.queryParamName),
      isVscodeWebview: isVscodeWebview(),
      prefersDark: window.matchMedia("(prefers-color-scheme: dark)").matches,
    });

    // Variant-only path: in a webview we never touch the host's base theme
    // (we can't re-skin `--vscode-*` reliably), but Event Colors is just a
    // `data-theme-variant` flip and is safe to apply on top.
    if (result.kind === "apply-variant-only") {
      if (result.variant === "readable") {
        document.documentElement.setAttribute("data-theme-variant", "readable");
      } else {
        document.documentElement.removeAttribute("data-theme-variant");
      }
      if (result.hostIsDark !== null) {
        setDocumentBaseScheme(result.hostIsDark);
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (result.kind === "apply") {
      // data-* attributes belong on <html> (CSS gates `:root[data-bs-theme]`);
      // the vscode-* class belongs on <body> (CSS gates `body[class^=...]`).
      // Splitting elements here is deliberate — keep them in lockstep.
      setDocumentBaseScheme(result.isDark);

      // The readable skin is a small token-override block keyed on this
      // attribute (see base.css); absent attribute = the design-consistent
      // default, so we remove rather than set "default".
      if (result.variant === "readable") {
        document.documentElement.setAttribute("data-theme-variant", "readable");
      } else {
        document.documentElement.removeAttribute("data-theme-variant");
      }

      // Only `vscode-dark` is toggled (never a `vscode-light` class): in light
      // standalone the bridge stays OFF so the `:root` Bootstrap + light
      // `--vscode-*` defaults apply, matching scout's long-shipped behavior.
      // Adding `vscode-light` would activate the bridge in light mode and
      // silently re-skin every `--bs-*` token.
      if (result.toggleBodyClass) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- intentional: document.body is null while parsing <head>
        document.body?.classList.toggle("vscode-dark", result.isDark);
      }
    }
    // No explicit React notification needed: the resolved theme lives in
    // `<html data-bs-theme>`, and useResolvedIsDark observes that attribute.
  };

  currentApplyTheme = applyTheme;
  window.__APPLY_BROWSER_THEME__ = applyTheme;

  if (!mediaListenerInstalled) {
    mediaListenerInstalled = true;
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", () => currentApplyTheme?.());
  }

  // Cross-tab sync (another tab wrote a new preference) is the app's concern:
  // it owns the storage key, so its keyed `storage` listener re-applies via
  // `window.__APPLY_BROWSER_THEME__` and rehydrates its store. Keeping it out
  // of here avoids re-applying on every unrelated localStorage write.

  // VS Code/Cursor swaps `vscode-dark` ↔ `vscode-light` on <body> when the
  // user changes the host theme. Re-apply so `data-bs-theme` / variant tokens
  // follow; the React icon then updates by observing that `data-bs-theme`.
  installBodyClassObserver();

  return applyTheme;
};
